/**
 * TradingView resolution ↔ Bullrun {@link Timeframe} mapping.
 *
 * The backend's smallest configured resolution is 5-minute (see
 * `CHART_TIMEFRAME_CONFIGS` in `domains/common/src/providers/data/tokens/types.ts`).
 * `"1"` (1-minute) is intentionally **omitted** from {@link
 * BULLRUN_TV_SUPPORTED_RESOLUTIONS} — serving 5m bars under a "1m" label
 * would silently mislabel the chart's time axis.
 */
import { Timeframe } from "../../bullrun/enums.js";
// `"15"` (15-minute) is intentionally absent: the backend's `ChartTimeframe`
// enum jumps from FIVE_MINUTES to ONE_HOUR, so a TV `"15"` toolbar selection
// would silently serve 1h bars under a "15m" label. If a 15-minute backend
// resolution is added later, restore it here (and to PREDICTION_SUPPORTED_RESOLUTIONS).
export const BULLRUN_TV_SUPPORTED_RESOLUTIONS = ["5", "60", "240", "D", "W", "M"];
// Perps (Hyperliquid candleSnapshot) support W/M natively; tokens and
// predictions do not — their backend `ChartTimeframe` bundles top out at daily
// bars, so W/M would have no correctly-grained source. Exposed per-kind in
// `buildLibrarySymbolInfo`.
export const TOKEN_SUPPORTED_RESOLUTIONS = ["5", "60", "240", "D"];
export const PREDICTION_SUPPORTED_RESOLUTIONS = ["5", "60", "D"];
const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const WEEK_MS = 7 * DAY_MS;
const MONTH_MS = 30 * DAY_MS;
/**
 * Wall-clock duration of one bar at a given TV resolution. Used by
 * `subscribeBars` to size each live bar window.
 *
 * `D`, `W`, `M` are calendar units in TV's contract but we treat them as
 * fixed-length windows for live aggregation. To keep the live grid aligned
 * with the backend's calendar-aligned `getBars` timestamps, `subscribeBars`
 * does NOT floor ticks from the unix epoch (which lands W on Thursdays and a
 * 30-day "month" off any real month). It snaps relative to the last historical
 * bar's open time instead, so this duration only sets the bar *width*, not its
 * phase.
 */
export function resolutionToBarDurationMs(resolution) {
    switch (resolution) {
        // "1" and "15" are intentionally absent from the supported-resolution lists
        // (see the comments at the top of this file), so TV never requests them.
        // Any unexpected numeric resolution still resolves correctly via `default`.
        case "5":
            return 5 * MINUTE_MS;
        case "60":
            return HOUR_MS;
        case "240":
            return 4 * HOUR_MS;
        case "D":
            return DAY_MS;
        case "W":
            return WEEK_MS;
        case "M":
            return MONTH_MS;
        default: {
            const minutes = Number(resolution);
            if (Number.isFinite(minutes) && minutes > 0)
                return minutes * MINUTE_MS;
            return HOUR_MS;
        }
    }
}
export function resolutionToTimeframe(resolution) {
    switch (resolution) {
        case "5":
            return Timeframe.FIVE_MINUTES;
        case "60":
            return Timeframe.ONE_HOUR;
        case "240":
            return Timeframe.FOUR_HOURS;
        case "D":
            return Timeframe.ONE_DAY;
        case "W":
            return Timeframe.ONE_WEEK;
        case "M":
            return Timeframe.ONE_MONTH;
        default:
            return Timeframe.ONE_DAY;
    }
}
